import { useState } from "react";
import { api } from "../api";
import { useAuth } from "../auth";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { KeyRound, Loader2, Mail, ShieldCheck, Building2 } from "lucide-react";

export default function Profile() {
  const { user } = useAuth();
  const [cur, setCur] = useState("");
  const [n1, setN1] = useState("");
  const [n2, setN2] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (n1 !== n2) return toast.error("Passwords do not match");
    if (n1.length < 6) return toast.error("Password must be at least 6 characters");
    setBusy(true);
    try {
      const r = await api.post("/auth/change-password", { current_password: cur, new_password: n1 });
      if (r.data?.token) {
        localStorage.setItem("rms_token", r.data.token);
      }
      toast.success("Password changed");
      setCur(""); setN1(""); setN2("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  if (!user) return null;

  const initials = user.employee_name.split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase();

  return (
    <div className="space-y-6 max-w-3xl">
      <div>
        <div className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground">Account</div>
        <h1 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight mt-1">Profile</h1>
      </div>

      <div className="rounded-xl border border-border bg-card p-6 flex items-center gap-5">
        <div className="w-16 h-16 rounded-full bg-primary text-primary-foreground grid place-items-center font-heading font-semibold text-xl">
          {initials}
        </div>
        <div className="flex-1">
          <div className="font-heading text-xl font-semibold">{user.employee_name}</div>
          <div className="text-sm text-muted-foreground flex items-center gap-2 mt-1"><Mail className="w-3.5 h-3.5" /> {user.email}</div>
          <div className="flex items-center gap-2 mt-2">
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full bg-primary/10 text-primary text-xs font-medium">
              <ShieldCheck className="w-3 h-3 mr-1" /> {user.role}
            </span>
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full bg-muted text-foreground/70 text-xs font-medium">
              <Building2 className="w-3 h-3 mr-1" /> {user.entity}
            </span>
          </div>
        </div>
      </div>

      <div className="rounded-xl border border-border bg-card p-6">
        <div className="flex items-start gap-3 mb-5">
          <div className="w-10 h-10 rounded-lg bg-primary/10 text-primary grid place-items-center"><KeyRound className="w-5 h-5" /></div>
          <div>
            <div className="font-medium">Change Password</div>
            <p className="text-xs text-muted-foreground mt-0.5">Use a unique password you don't use elsewhere.</p>
          </div>
        </div>
        <form onSubmit={submit} className="grid sm:grid-cols-2 gap-4 max-w-xl" data-testid="change-password-form">
          <div className="sm:col-span-2">
            <Label className="text-xs text-muted-foreground mb-1.5 block">Current password</Label>
            <Input type="password" required value={cur} onChange={(e) => setCur(e.target.value)} data-testid="cur-pwd" />
          </div>
          <div>
            <Label className="text-xs text-muted-foreground mb-1.5 block">New password</Label>
            <Input type="password" required minLength={6} value={n1} onChange={(e) => setN1(e.target.value)} data-testid="new-pwd" />
          </div>
          <div>
            <Label className="text-xs text-muted-foreground mb-1.5 block">Confirm new password</Label>
            <Input type="password" required minLength={6} value={n2} onChange={(e) => setN2(e.target.value)} data-testid="new-pwd-2" />
          </div>
          <div className="sm:col-span-2">
            <Button type="submit" disabled={busy} data-testid="change-pwd-submit">
              {busy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Update password
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
